import type {
  DocumentNode,
  LazyQueryHookOptions,
  OperationVariables,
  QueryTuple,
  TypedDocumentNode,
} from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

import { logGraphqlError } from './logGraphqlError';

export function useLazyQueryWithErrorLogging<TVariables extends OperationVariables, TData = unknown>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<TData, TVariables>,
): QueryTuple<TData, TVariables> {
  const { variables, ...hookOptions } = options ?? {};
  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  const [execute, result] = useLazyQuery(query, hookOptions);
  const advisorId = loggedInAsAdvisorID();
  const auth = useAuth();

  useEffect(() => {
    if (result.error) {
      logGraphqlError(query, result.error, { userId: auth.user?.id, advisorId });
    }
  }, [result.error, auth.user?.id, advisorId, query]);

  const wrappedExecute = useCallback(
    (executeOptions?: Parameters<typeof execute>[0]) => {
      return execute({
        variables: variablesRef.current,
        ...executeOptions,
      });
    },
    [execute],
  );

  return [wrappedExecute, result];
}
